import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Button, Checkbox, ListItemText, Menu, MenuItem } from '@mui/material';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MULTI_AXIS_MAX_METRICS, motorChartButtonI18nKey } from '../../Graphs/sensor-utils';
import type { MotorDataPickerEntry } from '../../../utils/electronicsDashboardUtils';

export interface ChooseMotorDataToolbarButtonProps {
  entries: MotorDataPickerEntry[];
  selectedMetrics: string[];
  onSelectedMetricsChange: (metrics: string[]) => void;
  disabled?: boolean;
  maxMetrics?: number;
}

const ChooseMotorDataToolbarButton = ({
  entries,
  selectedMetrics,
  onSelectedMetricsChange,
  disabled,
  maxMetrics = MULTI_AXIS_MAX_METRICS,
}: ChooseMotorDataToolbarButtonProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const availableMetrics = useMemo(() => entries.map((entry) => entry.metric), [entries]);
  const selectableMetrics = useMemo(() => availableMetrics.slice(0, maxMetrics), [availableMetrics, maxMetrics]);
  const selectedSet = useMemo(() => new Set(selectedMetrics), [selectedMetrics]);
  const allSelected = selectableMetrics.length > 0 && selectableMetrics.every((metric) => selectedSet.has(metric));
  const someSelected = selectableMetrics.some((metric) => selectedSet.has(metric));

  if (entries.length <= 1) return null;

  const commitMetrics = (metrics: string[]) => {
    const unique = [...new Set(metrics)].filter((metric) => availableMetrics.includes(metric));
    if (!unique.length) return;
    onSelectedMetricsChange(unique.slice(0, maxMetrics));
  };

  const handleToggleAll = () => {
    commitMetrics(allSelected ? [availableMetrics[0]] : selectableMetrics);
  };

  const handleToggleMetric = (metric: string) => {
    if (selectedSet.has(metric)) {
      const next = selectedMetrics.filter((value) => value !== metric);
      if (!next.length) return;
      commitMetrics(next);
      return;
    }
    if (selectedMetrics.length >= maxMetrics) return;
    commitMetrics([...selectedMetrics, metric]);
  };

  const atMaxSelection = selectedMetrics.length >= maxMetrics;

  const metricLabel = (metric: string) => t(motorChartButtonI18nKey(metric), metric);

  return (
    <>
      <Button
        size="small"
        color="inherit"
        variant="text"
        disabled={disabled}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ flexShrink: 0, minWidth: 0, px: 1, textTransform: 'none' }}
      >
        <Trans i18nKey="dashboard.motorWidget.chooseData">Choose Data</Trans>
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} slotProps={{ list: { dense: true, sx: { minWidth: 280 } } }}>
        <MenuItem onClick={handleToggleAll}>
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            tabIndex={-1}
            disableRipple
          />
          <ListItemText primary={t('dashboard.motorWidget.chooseAllData', 'Choose all')} />
        </MenuItem>
        {entries.map((entry) => {
          const checked = selectedSet.has(entry.metric);
          const selectionDisabled = !checked && atMaxSelection;
          return (
            <MenuItem key={entry.metric} disabled={selectionDisabled} onClick={() => handleToggleMetric(entry.metric)}>
              <Checkbox
                checked={checked}
                disabled={selectionDisabled}
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary={metricLabel(entry.metric)} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default ChooseMotorDataToolbarButton;
